import { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function Quote() {
  const { user } = useAuth();
  const [f, setF] = useState({
    product_type: "printing", subject: "", details: "",
    quantity: 100, dimensions: "", material: "", finishing: "",
    deadline: "", delivery_method: "shipping", budget: "",
    contact_email: "", contact_name: "", contact_phone: "",
  });
  const [artworkFile, setArtworkFile] = useState(null);
  const [artworkUploading, setArtworkUploading] = useState(false);
  const [artworkFileId, setArtworkFileId] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const uploadArtwork = async () => {
    if (!artworkFile) return null;
    setArtworkUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", artworkFile);
      const { data } = await api.post("/uploads/artwork", fd);
      setArtworkFileId(data.id);
      toast.success("Artwork uploaded");
      return data.id;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Upload failed");
      return null;
    } finally { setArtworkUploading(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let fileId = artworkFileId;
      if (artworkFile && !fileId && user) {
        fileId = await uploadArtwork();
      }
      const payload = {
        ...f,
        quantity: Number(f.quantity) || null,
        budget: f.budget ? Number(f.budget) : null,
        artwork_file_id: fileId || null,
      };
      await api.post("/quotes", payload);
      toast.success("Quote request received");
      setSubmitted(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to submit");
    } finally { setLoading(false); }
  };

  if (submitted) return (
    <section className="max-w-[720px] mx-auto px-6 py-24 text-center">
      <p className="font-mono text-xs tracking-[0.3em] uppercase text-[#C5A059]">Received</p>
      <h1 className="font-serif text-5xl mt-4">Thank you.</h1>
      <p className="mt-6 text-[#555]">We'll be in touch within one business day with pricing.</p>
    </section>
  );

  return (
    <section className="max-w-[900px] mx-auto px-6 py-24">
      <p className="text-xs font-mono tracking-[0.3em] uppercase text-[#C5A059]">Quote</p>
      <h1 className="font-serif text-5xl md:text-7xl mt-3">Request a quote.</h1>
      <p className="mt-6 text-[#555] max-w-xl">Tell us about your project. We'll respond with custom pricing within one business day.</p>

      <form onSubmit={submit} className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="text-xs font-mono tracking-[0.3em] uppercase">Product / Service Type</label>
          <select value={f.product_type} onChange={set("product_type")} data-testid="quote-type" className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent">
            <option value="printing">Printing</option>
            <option value="graphic_design">Graphic Design</option>
            <option value="website_design">Website Design</option>
            <option value="mixed">Mixed / Multiple</option>
          </select>
        </div>
        <div>
          <label className="text-xs font-mono tracking-[0.3em] uppercase">Subject</label>
          <input required value={f.subject} onChange={set("subject")} data-testid="quote-subject" className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent" placeholder="e.g. 500 letterpress business cards" />
        </div>

        <div className="md:col-span-2">
          <label className="text-xs font-mono tracking-[0.3em] uppercase">Project Details</label>
          <textarea required rows={4} value={f.details} onChange={set("details")} data-testid="quote-details" className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent resize-none" placeholder="Describe your project, goals, and any references…" />
        </div>

        <div>
          <label className="text-xs font-mono tracking-[0.3em] uppercase">Quantity</label>
          <input type="number" value={f.quantity} onChange={set("quantity")} data-testid="quote-qty" className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent" />
        </div>
        <div>
          <label className="text-xs font-mono tracking-[0.3em] uppercase">Dimensions</label>
          <input value={f.dimensions} onChange={set("dimensions")} data-testid="quote-dimensions" placeholder="e.g. 3.5 x 2 in" className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent" />
        </div>
        <div>
          <label className="text-xs font-mono tracking-[0.3em] uppercase">Material / Paper</label>
          <input value={f.material} onChange={set("material")} data-testid="quote-material" placeholder="e.g. 350gsm cardstock" className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent" />
        </div>
        <div>
          <label className="text-xs font-mono tracking-[0.3em] uppercase">Finishing</label>
          <input value={f.finishing} onChange={set("finishing")} data-testid="quote-finishing" placeholder="e.g. matte + gold foil" className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent" />
        </div>

        <div>
          <label className="text-xs font-mono tracking-[0.3em] uppercase">Deadline</label>
          <input type="date" value={f.deadline} onChange={set("deadline")} data-testid="quote-deadline" className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent" />
        </div>
        <div>
          <label className="text-xs font-mono tracking-[0.3em] uppercase">Delivery Method</label>
          <select value={f.delivery_method} onChange={set("delivery_method")} data-testid="quote-delivery" className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent">
            <option value="shipping">Ship to me</option>
            <option value="pickup">Studio pickup</option>
            <option value="local_delivery">Local delivery</option>
            <option value="digital">Digital only</option>
          </select>
        </div>

        <div>
          <label className="text-xs font-mono tracking-[0.3em] uppercase">Budget (USD)</label>
          <input type="number" value={f.budget} onChange={set("budget")} data-testid="quote-budget" className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent" />
        </div>
        <div>
          <label className="text-xs font-mono tracking-[0.3em] uppercase">Phone (optional)</label>
          <input value={f.contact_phone} onChange={set("contact_phone")} data-testid="quote-phone" className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent" />
        </div>

        <div>
          <label className="text-xs font-mono tracking-[0.3em] uppercase">Your Name</label>
          <input required value={f.contact_name} onChange={set("contact_name")} data-testid="quote-name" className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent" />
        </div>
        <div>
          <label className="text-xs font-mono tracking-[0.3em] uppercase">Email</label>
          <input required type="email" value={f.contact_email} onChange={set("contact_email")} data-testid="quote-email" className="mt-2 w-full border-b border-[#E5E5E5] focus:border-[#C5A059] outline-none py-3 bg-transparent" />
        </div>

        <div className="md:col-span-2">
          <label className="text-xs font-mono tracking-[0.3em] uppercase">Attach Artwork (optional)</label>
          <p className="text-xs text-[#888] mt-1">PDF, PNG, JPG, SVG, AI, EPS, PSD, ZIP — up to 50MB. {!user && <span className="text-[#C5A059]">Sign in to attach files.</span>}</p>
          {user && (
            <div className="mt-3 flex flex-wrap gap-3 items-center">
              <label className="inline-block text-xs font-mono tracking-[0.25em] uppercase border border-[#0a0a0a] px-4 py-3 cursor-pointer hover:bg-[#FAFAFA]" data-testid="quote-artwork-label">
                {artworkFile ? artworkFile.name : "Choose File"}
                <input type="file" hidden accept=".pdf,.png,.jpg,.jpeg,.svg,.ai,.eps,.psd,.zip,.tiff,.tif,.gif,.webp" onChange={e => setArtworkFile(e.target.files[0] || null)} data-testid="quote-artwork" />
              </label>
              {artworkFile && !artworkFileId && (
                <button type="button" onClick={uploadArtwork} disabled={artworkUploading} data-testid="quote-artwork-upload" className="text-xs font-mono tracking-[0.25em] uppercase bg-[#0a0a0a] text-white px-4 py-3">
                  {artworkUploading ? "Uploading..." : "Upload"}
                </button>
              )}
              {artworkFileId && <span className="text-xs text-[#1F4D2A] font-mono">✓ Uploaded</span>}
            </div>
          )}
        </div>

        <div className="md:col-span-2">
          <button disabled={loading} data-testid="quote-submit" className="w-full md:w-auto bg-[#0a0a0a] text-white hover:bg-[#C5A059] hover:text-[#0a0a0a] transition-colors px-12 py-4 text-xs tracking-[0.25em] uppercase font-bold disabled:opacity-40">
            {loading ? "Submitting..." : "Submit Request"}
          </button>
        </div>
      </form>
    </section>
  );
}
